import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LogOut, LayoutDashboard, BookOpen, User, ShieldCheck } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header
      data-testid="global-navbar"
      className="sticky top-0 z-40 border-b border-zinc-200 bg-white/70 backdrop-blur-xl"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img
            src="/imcac_logo.png"
            alt="IMCAC"
            className="h-10 w-10 object-contain"
          />
          <div className="leading-tight">
            <div className="font-display font-bold text-sm tracking-tight">
              IMCAC<span className="text-red-800">.</span>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              Department of Occupational Safety Programs
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-zinc-700">
          <Link to="/" className="hover:text-red-600 transition-colors">Home</Link>
          {user && <Link to="/dashboard" className="hover:text-red-600 transition-colors">Dashboard</Link>}
          {user && <Link to="/course" className="hover:text-red-600 transition-colors">Course</Link>}
        </nav>

        <div className="flex items-center gap-3">
          {!user ? (
            <>
              <Button
                variant="ghost"
                onClick={() => navigate("/login")}
                data-testid="navbar-login-btn"
                className="text-sm"
              >
                Log in
              </Button>
              <Button
                onClick={() => navigate("/register")}
                data-testid="navbar-signup-btn"
                className="bg-red-600 hover:bg-red-700 text-white text-sm"
              >
                Get certified
              </Button>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="navbar-user-menu"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-zinc-200 hover:border-zinc-300"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="h-6 w-6 rounded-full" />
                  ) : (
                    <div className="h-6 w-6 rounded-full bg-zinc-900 text-white flex items-center justify-center text-xs font-bold">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </div>
                  )}
                  <span className="text-sm font-medium hidden sm:inline">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/course")} data-testid="menu-course">
                  <BookOpen className="h-4 w-4 mr-2" /> Course
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <ShieldCheck className="h-4 w-4 mr-2 text-red-600" /> Admin portal
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => { await logout(); navigate("/"); }}
                  data-testid="menu-logout"
                  className="text-red-600 focus:text-red-700"
                >
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
